import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export default function AboutPage() {
  return (
    <div className="container mx-auto px-4 py-12 space-y-16">
      <section className="text-center max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold mb-6">About Homespaces</h1>
        <p className="text-xl text-muted-foreground">
          We&apos;re on a mission to make finding your perfect home as simple and
          stress-free as possible.
        </p>
      </section>

      <section className="grid md:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-3xl font-bold mb-4">Our Story</h2>
          <p className="mb-4">
            Founded in 2018, Homespaces began with a simple idea: finding a home
            should be exciting, not exhausting. Our founders experienced
            firsthand the challenges of the rental market and decided to create
            a solution that puts renters first.
          </p>
          <p>
            Today, we&apos;ve helped thousands of people find their perfect homes
            across the country, with a focus on transparency, quality listings,
            and exceptional customer service.
          </p>
        </div>
        <div className="relative h-[400px] rounded-lg overflow-hidden">
          <img src="/placeholder.svg?height=400&width=600"
            alt="Homespaces office" className="object-cover"
          />
        </div>
      </section>

      <section>
        <h2 className="text-3xl font-bold mb-8 text-center">Our Values</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <ValueCard
            title="Transparency"
            description="We believe in complete honesty about properties, neighborhoods, and pricing."
          />
          <ValueCard
            title="Innovation"
            description="We constantly improve our platform with new features and technologies."
          />
          <ValueCard
            title="Community"
            description="We're building more than a platform—we're creating communities."
          />
        </div>
      </section>

      <section>
        <h2 className="text-3xl font-bold mb-8 text-center">Meet Our Team</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          <TeamMember
            name="Alex Johnson"
            role="CEO & Co-Founder"
            bio="Former real estate agent with 10+ years of experience in the housing market."
            image="/placeholder.svg?height=200&width=200"
          />
          <TeamMember
            name="Sarah Chen"
            role="CTO & Co-Founder"
            bio="Tech enthusiast with a background in UX design and software development."
            image="/placeholder.svg?height=200&width=200"
          />
          <TeamMember
            name="Michael Rodriguez"
            role="Head of Operations"
            bio="Streamlines our processes to ensure a smooth experience for all users."
            image="/placeholder.svg?height=200&width=200"
          />
          <TeamMember
            name="Priya Patel"
            role="Customer Success Lead"
            bio="Dedicated to providing exceptional support to our users."
            image="/placeholder.svg?height=200&width=200"
          />
        </div>
      </section>

      <section className="bg-muted rounded-xl p-8">
        <h2 className="text-3xl font-bold mb-8 text-center">Our Impact</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <StatCard number="10,000+" label="Happy Tenants" />
          <StatCard number="5,000+" label="Properties Listed" />
          <StatCard number="500+" label="Landlord Partners" />
          <StatCard number="50+" label="Cities Covered" />
        </div>
      </section>
    </div>
  );
}

interface ValueCardProps {
  title: string;
  description: string;
}

function ValueCard({ title, description }: ValueCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p>{description}</p>
      </CardContent>
    </Card>
  );
}

interface TeamMemberProps {
  name: string;
  role: string;
  bio: string;
  image: string;
}

function TeamMember({ name, role, bio, image }: TeamMemberProps) {
  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <div className="text-center cursor-pointer">
          <Avatar className="h-32 w-32 mx-auto mb-4">
            <AvatarImage src={image} alt={name} />
            <AvatarFallback>
              {name
                .split(" ")
                .map((n: string) => n[0])
                .join("")}
            </AvatarFallback>
          </Avatar>
          <h3 className="font-semibold text-lg">{name}</h3>
          <p className="text-muted-foreground">{role}</p>
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-80">
        <div className="flex justify-between space-x-4">
          <Avatar>
            <AvatarImage src={image} />
            <AvatarFallback>
              {name
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </AvatarFallback>
          </Avatar>
          <div className="space-y-1">
            <h4 className="text-sm font-semibold">{name}</h4>
            <p className="text-sm text-muted-foreground">{role}</p>
            <p className="text-sm">{bio}</p>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

interface StatCardProps {
  number: string;
  label: string;
}

function StatCard({ number, label }: StatCardProps) {
  return (
    <div>
      <p className="text-4xl font-bold text-primary">{number}</p>
      <p className="text-muted-foreground">{label}</p>
    </div>
  );
}
